import { useState, useEffect } from "react";
import api, { formatBRL, initials, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Trophy, Medal, Target, TrendingUp, TrendingDown, DollarSign, Percent, Users, Minus, Swords, Phone, MessageCircle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const CHART_COLORS = ["#2563EB", "#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

/**
 * Badge de variação YoY. `positiveIsGood=false` inverte a semântica (ex: Falta baixando = bom).
 */
export function DeltaBadge({ current, previous, format = "num", label, positiveIsGood = true, testid }) {
  if (previous === undefined || previous === null) return null;
  if (!previous) return null; // sem base de comparação (ano anterior sem dados) → não exibe nada
  const delta = current - previous;
  const pct = (delta / Math.abs(previous)) * 100;
  const up = delta > 0;
  const flat = Math.abs(pct) < 0.5;
  const good = flat ? true : (up === positiveIsGood);
  const cls = flat
    ? "bg-slate-100 text-slate-600"
    : good
      ? "bg-emerald-100 text-emerald-700"
      : "bg-red-100 text-red-700";
  const Ico = flat ? Minus : up ? TrendingUp : TrendingDown;
  const sign = up ? "+" : "";
  const val = format === "brl"
    ? (up ? "+" : "-") + formatBRL(Math.abs(delta)).replace("R$", "R$").trim()
    : `${sign}${delta}`;
  const title = label ? `${label}: anterior ${format === "brl" ? formatBRL(previous) : previous}` : "";
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cls}`}
      title={title}
      data-testid={testid}
    >
      <Ico className="w-3 h-3" />
      {flat ? "estável" : `${sign}${pct.toFixed(0)}%`}
    </span>
  );
}

export function KPICard({ icon: Icon, label, value, sub, tone = "default", delta, testid }) {
  const toneMap = {
    default: "border-slate-200 bg-white text-slate-900",
    green: "border-emerald-300 bg-emerald-50 text-emerald-900",
    yellow: "border-amber-300 bg-amber-50 text-amber-900",
    red: "border-red-300 bg-red-50 text-red-900",
    blue: "border-blue-200 bg-white text-slate-900",
  };
  const iconTone = {
    default: "bg-blue-50 text-blue-600",
    green: "bg-emerald-500 text-white",
    yellow: "bg-amber-500 text-white",
    red: "bg-red-500 text-white",
    blue: "bg-blue-50 text-blue-600",
  };
  return (
    <Card className={`p-5 md:p-6 rounded-2xl border shadow-sm ${toneMap[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-widest opacity-80">{label}</div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconTone[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div
        className="font-display font-bold tracking-tighter tabular-nums mt-3 whitespace-nowrap overflow-hidden text-ellipsis"
        style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.5rem)", lineHeight: 1.1 }}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </div>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        {sub && <div className="text-xs opacity-80">{sub}</div>}
        {delta}
      </div>
    </Card>
  );
}

export function ProgressBar({ value, total, testid, big = false }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  const cls = pct >= 100 ? "win" : pct < 40 ? "warn" : "";
  return (
    <div className={`progress-track ${big ? "h-4" : "h-3"}`} data-testid={testid}>
      <div className={`progress-fill ${cls}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// Iter 78 · faixa: vendedora oficial do núcleo filtrado × quem vendeu vindo de outro núcleo
export function NucleoTag({ item, small = false }) {
  if (item?.do_nucleo === undefined || item?.do_nucleo === null) return null;
  const sz = small ? "text-[9px] px-1.5" : "text-[10px] px-2";
  return item.do_nucleo ? (
    <span className={`inline-block ${sz} py-0.5 rounded-full font-bold uppercase tracking-wide bg-blue-600 text-white whitespace-nowrap`}
      data-testid={`tag-do-nucleo-${item.vendedora_id}`}>
      Vendedora do núcleo {item.nucleo_nome || ""}
    </span>
  ) : (
    <span className={`inline-block ${sz} py-0.5 rounded-full font-semibold bg-slate-100 text-slate-500 whitespace-nowrap`}
      data-testid={`tag-outro-nucleo-${item.vendedora_id}`}>
      {item.nucleo_nome ? `de ${item.nucleo_nome}` : "outro núcleo"}
    </span>
  );
}

export function Podium({ items }) {
  const top3 = items.slice(0, 3);
  const order = [1, 0, 2];
  const heights = ["h-32", "h-40", "h-24"];
  const colors = ["from-slate-300 to-slate-400", "from-yellow-400 to-amber-500", "from-orange-400 to-orange-600"];
  return (
    <div className="flex items-end justify-center gap-3 md:gap-6 py-4">
      {order.map((i, idx) => {
        const item = top3[i];
        if (!item) return <div key={`empty-${idx}`} className="flex-1 max-w-[120px]" />;
        return (
          <div key={`pos-${i}`} className="flex-1 max-w-[140px] flex flex-col items-center">
            <Avatar className="w-14 h-14 md:w-16 md:h-16 ring-4 ring-white shadow-lg mb-2">
              <AvatarImage src={resolveAvatar(item.avatar_url)} alt={item.nome} />
              <AvatarFallback className={`bg-gradient-to-br ${colors[idx]} text-white font-bold`}>
                {initials(item.nome)}
              </AvatarFallback>
            </Avatar>
            <div className="text-xs md:text-sm font-semibold text-slate-800 text-center line-clamp-1">
              {item.nome}
            </div>
            <NucleoTag item={item} small />
            <div className="text-xs text-slate-500 mb-2">{formatBRL(item.valor)}</div>
            <div
              className={`w-full ${heights[idx]} rounded-t-xl bg-gradient-to-b ${colors[idx]} flex items-start justify-center pt-2 text-white font-display font-bold text-2xl`}
              data-testid={`podium-${idx}`}
            >
              {i + 1}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function mesAtualStr() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function mesesDisponiveis() {
  // Iter 40: gera 12 meses passados + mês atual + 6 meses futuros (para permitir
  // planejamento de cursos que ocorrem nos próximos meses até dezembro/etc).
  const now = new Date();
  const arr = [];
  // Meses futuros primeiro (do mais distante ao mais próximo) → mês atual → passados
  for (let i = 6; i >= 1; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return arr;
}

function anosDisponiveis() {
  const y = new Date().getFullYear();
  return [y, y - 1, y - 2, y - 3];
}

export default function DashboardGeral() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [dataPrev, setDataPrev] = useState(null); // YoY quando modo ano
  const [nucleos, setNucleos] = useState([]);
  const [vendedoras, setVendedoras] = useState([]);
  const [periodoTipo, setPeriodoTipo] = useState("mes"); // "mes" | "ano"
  const [mesAno, setMesAno] = useState(mesAtualStr());
  const [anoSel, setAnoSel] = useState(anosDisponiveis()[0]);
  // Iter 76 · diretor com núcleo já abre filtrado no próprio núcleo; Master (sem núcleo) abre em "todos"
  const [filtroNucleo, setFiltroNucleo] = useState(user?.nucleo_id || "todos");
  const [filtroVend, setFiltroVend] = useState("todos");
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [tentativa, setTentativa] = useState(0); // trigger para retry

  useEffect(() => {
    api.get("/nucleos").then((r) => setNucleos(r.data));
    api.get("/users").then((r) => setVendedoras((r.data || []).filter(u => u.role === "vendedora"))).catch(() => {});
  }, []);

  useEffect(() => {
    setCarregando(true);
    setErro(null);
    const p = new URLSearchParams();
    if (periodoTipo === "ano") p.set("ano", anoSel);
    else p.set("mes_ano", mesAno);
    if (filtroNucleo !== "todos") p.set("nucleo_id", filtroNucleo);
    if (filtroVend !== "todos") p.set("vendedora_id", filtroVend);
    api.get(`/dashboards/geral?${p.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setErro(e?.response?.data?.detail || e?.message || "Falha ao carregar painel");
        setData(null);
      })
      .finally(() => setCarregando(false));

    // YoY: também no modo mês (mesmo mês do ano anterior)
    const pp = new URLSearchParams();
    if (periodoTipo === "ano") {
      pp.set("ano", anoSel - 1);
    } else {
      // Ex.: "2026-07" -> "2025-07"
      const [ys, ms] = mesAno.split("-");
      pp.set("mes_ano", `${Number(ys) - 1}-${ms}`);
    }
    if (filtroNucleo !== "todos") pp.set("nucleo_id", filtroNucleo);
    if (filtroVend !== "todos") pp.set("vendedora_id", filtroVend);
    api.get(`/dashboards/geral?${pp.toString()}`).then((r) => setDataPrev(r.data)).catch(() => setDataPrev(null));
  }, [mesAno, anoSel, periodoTipo, filtroNucleo, filtroVend, tentativa]);

  if (erro) {
    return (
      <div className="max-w-lg mx-auto mt-8 p-6 bg-white rounded-2xl border border-red-200 shadow-sm" data-testid="dashboard-erro">
        <div className="text-red-700 font-bold mb-2">Falha ao carregar painel</div>
        <div className="text-sm text-slate-600 mb-4">{erro}</div>
        <button
          onClick={() => setTentativa((t) => t + 1)}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700"
          data-testid="dashboard-retry"
        >
          Tentar novamente
        </button>
      </div>
    );
  }
  if (!data) return <div className="text-slate-500" data-testid="dashboard-loading">{carregando ? "Carregando painel..." : "Sem dados"}</div>;

  const pct = data.meta_receita ? (data.realizado / data.meta_receita) * 100 : 0;
  const realizadoTone = pct >= 100 ? "green" : pct >= 70 ? "yellow" : "red";

  // Filtragem por núcleo (client-side complementar — backend já filtra se nucleo_id vier)
  const cursosFiltered = data.cursos;

  const t = {
    meta_inscritos: cursosFiltered.reduce((s, c) => s + c.meta_inscritos, 0),
    meta_receita: cursosFiltered.reduce((s, c) => s + c.meta_receita, 0),
    realizado: cursosFiltered.reduce((s, c) => s + c.realizado, 0),
    inscritos: cursosFiltered.reduce((s, c) => s + c.inscritos, 0),
    exp_min: cursosFiltered.reduce((s, c) => s + c.exp_min, 0),
    exp_max: cursosFiltered.reduce((s, c) => s + c.exp_max, 0),
    interessados: cursosFiltered.reduce((s, c) => s + c.interessados, 0),
    incertos: cursosFiltered.reduce((s, c) => s + c.incertos, 0),
    prospectados: cursosFiltered.reduce((s, c) => s + c.prospectados, 0),
  };
  const ticket_medio_f = t.inscritos ? t.realizado / t.inscritos : 0;
  const pct_f = t.meta_receita ? (t.realizado / t.meta_receita) * 100 : 0;
  const falta_f = Math.max(0, t.meta_receita - t.realizado);

  // Cálculo YoY — funciona tanto em modo ano quanto em modo mês (mesmo mês do ano anterior)
  const isYoY = !!dataPrev;
  const prev = isYoY ? {
    meta_receita: dataPrev.cursos.reduce((s, c) => s + c.meta_receita, 0),
    meta_inscritos: dataPrev.cursos.reduce((s, c) => s + c.meta_inscritos, 0),
    realizado: dataPrev.cursos.reduce((s, c) => s + c.realizado, 0),
    inscritos: dataPrev.cursos.reduce((s, c) => s + c.inscritos, 0),
  } : null;
  const prev_ticket = prev && prev.inscritos ? prev.realizado / prev.inscritos : 0;
  const prev_falta = prev ? Math.max(0, prev.meta_receita - prev.realizado) : 0;

  // Rótulo de comparação: "vs 2025" no modo ano, "vs Jul/2025" no modo mês
  const prevLabel = (() => {
    if (!isYoY) return "";
    if (periodoTipo === "ano") return `vs ${anoSel - 1}`;
    const [ys, ms] = mesAno.split("-");
    const nomesMes = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    return `vs ${nomesMes[Number(ms) - 1]}/${Number(ys) - 1}`;
  })();

  const rankingsFiltered = { vendas: data.ranking_vendas, prod: data.ranking_produtividade };

  const periodoLabel = data.modo === "ano" ? `Ano de ${data.mes_ano}` : data.mes_ano;

  // Evolução para YoY: mensal em modo ano, diária em modo mês
  const evolucaoYoY = (() => {
    if (!isYoY) return null;
    if (periodoTipo === "ano") {
      const monthly = { atual: {}, prev: {} };
      data.evolucao_diaria.forEach((e) => {
        const m = e.dia.slice(5, 7);
        monthly.atual[m] = (monthly.atual[m] || 0) + e.valor;
      });
      dataPrev.evolucao_diaria.forEach((e) => {
        const m = e.dia.slice(5, 7);
        monthly.prev[m] = (monthly.prev[m] || 0) + e.valor;
      });
      const ms = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
      const labels = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
      return ms.map((m, i) => ({
        mes: labels[i],
        atual: monthly.atual[m] || 0,
        anterior: monthly.prev[m] || 0,
      }));
    }
    // Modo mês: comparação dia-a-dia (dia 1..31)
    const daily = { atual: {}, prev: {} };
    data.evolucao_diaria.forEach((e) => {
      const d = Number(e.dia.slice(8, 10));
      daily.atual[d] = (daily.atual[d] || 0) + e.valor;
    });
    dataPrev.evolucao_diaria.forEach((e) => {
      const d = Number(e.dia.slice(8, 10));
      daily.prev[d] = (daily.prev[d] || 0) + e.valor;
    });
    return Array.from({ length: 31 }, (_, i) => {
      const d = i + 1;
      return {
        mes: String(d).padStart(2, "0"),
        atual: daily.atual[d] || 0,
        anterior: daily.prev[d] || 0,
      };
    });
  })();

  return (
    <div className="space-y-6" data-testid="dashboard-geral">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">
            {data.modo === "ano" ? "Consolidado do ano · " : "Consolidado do mês · "}{periodoLabel}
            {filtroNucleo !== "todos" && ` · ${nucleos.find(n => n.id === filtroNucleo)?.nome || ""}`}
            {filtroVend !== "todos" && ` · ${vendedoras.find(v => v.id === filtroVend)?.nome || ""}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {/* Vendedora — apenas diretoria/supervisão */}
          {user?.role !== "vendedora" && (
            <Select value={filtroVend} onValueChange={setFiltroVend}>
              <SelectTrigger className="w-44" data-testid="filter-vendedora"><SelectValue placeholder="Vendedora" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas as vendedoras</SelectItem>
                {vendedoras.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.codigo ? `${v.codigo} · ` : ""}{v.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Núcleo — apenas diretoria (supervisão/vendedora ficam presas ao próprio) */}
          {user?.role === "diretoria" && (
            <Select value={filtroNucleo} onValueChange={setFiltroNucleo}>
              <SelectTrigger className="w-36" data-testid="filter-nucleo"><SelectValue placeholder="Núcleo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os núcleos</SelectItem>
                {nucleos.map((n) => (<SelectItem key={n.id} value={n.id}>{n.nome}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          {/* Período: Mês/Ano toggle + valor */}
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="periodo-toggle">
            <button
              onClick={() => setPeriodoTipo("mes")}
              className={`px-3 py-1.5 text-xs font-bold ${periodoTipo === "mes" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="toggle-mes"
            >Mês</button>
            <button
              onClick={() => setPeriodoTipo("ano")}
              className={`px-3 py-1.5 text-xs font-bold ${periodoTipo === "ano" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="toggle-ano"
            >Ano</button>
          </div>
          {periodoTipo === "mes" ? (
            <Select value={mesAno} onValueChange={setMesAno}>
              <SelectTrigger className="w-32" data-testid="filter-mes"><SelectValue /></SelectTrigger>
              <SelectContent>
                {mesesDisponiveis().map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
              </SelectContent>
            </Select>
          ) : (
            <Select value={String(anoSel)} onValueChange={(v) => setAnoSel(Number(v))}>
              <SelectTrigger className="w-24" data-testid="filter-ano"><SelectValue /></SelectTrigger>
              <SelectContent>
                {anosDisponiveis().map((a) => (<SelectItem key={a} value={String(a)}>{a}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* KPIs — Ordem: Realizado, Inscritos, Ticket, Meta, Falta */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          icon={DollarSign} label="Realizado" value={formatBRL(t.realizado)}
          sub={`${pct_f.toFixed(0)}% da meta`}
          tone={pct_f >= 100 ? "green" : pct_f >= 70 ? "yellow" : "red"}
          delta={isYoY && <DeltaBadge current={t.realizado} previous={prev.realizado} format="brl" label={prevLabel} testid="delta-realizado" />}
          testid="kpi-realizado"
        />
        <KPICard
          icon={Trophy} label="Inscritos" value={t.inscritos} sub={`de ${t.meta_inscritos}`}
          delta={isYoY && <DeltaBadge current={t.inscritos} previous={prev.inscritos} label={prevLabel} testid="delta-inscritos" />}
          testid="kpi-inscritos"
        />
        <KPICard
          icon={Percent} label="Ticket Médio" value={formatBRL(ticket_medio_f)}
          delta={isYoY && <DeltaBadge current={ticket_medio_f} previous={prev_ticket} format="brl" label={prevLabel} testid="delta-ticket" />}
          testid="kpi-ticket"
        />
        <KPICard
          icon={Target} label={periodoTipo === "ano" ? "Meta do Ano" : "Meta do Mês"} value={formatBRL(t.meta_receita)}
          delta={isYoY && <DeltaBadge current={t.meta_receita} previous={prev.meta_receita} format="brl" label={prevLabel} testid="delta-meta" />}
          testid="kpi-meta"
        />
        <KPICard
          icon={TrendingUp} label="Falta" value={formatBRL(falta_f)}
          delta={isYoY && <DeltaBadge current={falta_f} previous={prev_falta} format="brl" positiveIsGood={false} label={prevLabel} testid="delta-falta" />}
          testid="kpi-falta"
        />
      </div>

      {/* Barra da meta */}
      <Card className="p-6 rounded-2xl">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-slate-700">Progresso {periodoTipo === "ano" ? "do ano" : "do mês"}</div>
          <div className="text-sm font-bold tabular-nums text-slate-900">{pct_f.toFixed(1)}%</div>
        </div>
        <ProgressBar value={t.realizado} total={t.meta_receita} big testid="progress-mes" />
      </Card>

      {/* Núcleos (somente diretoria, quando filtro = todos) */}
      {filtroNucleo === "todos" && user?.role === "diretoria" && (
        <div className="grid md:grid-cols-3 gap-4">
          {data.nucleos.map((n) => {
            const p = n.meta ? (n.realizado / n.meta) * 100 : 0;
            const prevN = isYoY ? dataPrev.nucleos.find(x => x.nucleo_id === n.nucleo_id) : null;
            return (
              <Card key={n.nucleo_id} className="p-5 rounded-2xl cursor-pointer hover:shadow-md" onClick={() => setFiltroNucleo(n.nucleo_id)} data-testid={`nucleo-${n.nucleo_id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{n.nucleo_nome}</div>
                  {isYoY && prevN && (
                    <DeltaBadge current={n.realizado} previous={prevN.realizado} format="brl" label={prevLabel} testid={`delta-nucleo-${n.nucleo_id}`} />
                  )}
                </div>
                <div
                  className="font-display font-bold tabular-nums mt-1 text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis"
                  style={{ fontSize: "clamp(1.25rem, 2vw, 1.875rem)", lineHeight: 1.15 }}
                  title={formatBRL(n.realizado)}
                >{formatBRL(n.realizado)}</div>
                <div className="text-xs text-slate-500 mb-3">de {formatBRL(n.meta)} — {p.toFixed(0)}%</div>
                <ProgressBar value={n.realizado} total={n.meta} />
              </Card>
            );
          })}
        </div>
      )}

      {/* CURSOS DO MÊS — ANTES do ranking, com cores nos cabeçalhos */}
      <Card className="p-6 rounded-2xl overflow-x-auto" data-testid="tabela-cursos">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl text-slate-900">
            {periodoTipo === "ano" ? "Cursos do ano" : "Cursos do mês"}
          </h2>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500"></span>≥100%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500"></span>≥50%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500"></span>&lt;50%</span>
          </div>
        </div>
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest">
              <th className="pb-2 px-3 py-2 text-white bg-slate-700 rounded-tl-lg">Curso / Núcleo</th>
              <th className="pb-2 px-3 py-2 text-white bg-blue-900 text-right">Meta insc.</th>
              <th className="pb-2 px-3 py-2 text-white bg-blue-900 text-right">Meta R$</th>
              <th className="pb-2 px-3 py-2 text-white bg-emerald-600 text-right">Realizado</th>
              <th className="pb-2 px-3 py-2 text-white bg-emerald-600 text-right">Inscritos</th>
              <th className="pb-2 px-3 py-2 text-white bg-emerald-600 text-right">Ticket</th>
              <th className="pb-2 px-3 py-2 text-white bg-emerald-600 text-right">% Meta</th>
              <th className="pb-2 px-3 py-2 text-white bg-amber-500 text-right">Exp (-)</th>
              <th className="pb-2 px-3 py-2 text-white bg-amber-500 text-right">Exp (+)</th>
              <th className="pb-2 px-3 py-2 text-white bg-sky-400 text-right">Interess.</th>
              <th className="pb-2 px-3 py-2 text-white bg-slate-900 text-right">Incertos</th>
              <th className="pb-2 px-3 py-2 text-white bg-slate-400 text-right rounded-tr-lg">Prospect.</th>
            </tr>
          </thead>
          <tbody>
            {cursosFiltered.map((c) => {
              const pctc = c.meta_receita ? (c.realizado / c.meta_receita) * 100 : 0;
              // Iter 36: ≥100% verde · ≥50% amarelo · <50% vermelho
              const bg = pctc >= 100 ? "bg-emerald-50" : pctc >= 50 ? "bg-amber-50" : "bg-red-50";
              const dot = pctc >= 100 ? "bg-emerald-500" : pctc >= 50 ? "bg-amber-500" : "bg-red-500";
              return (
                <tr
                  key={c.curso_id}
                  className={`border-b border-slate-100 hover:brightness-95 cursor-pointer ${bg}`}
                  onClick={() => nav(`/funil/${c.curso_id}`)}
                  data-testid={`curso-row-${c.curso_id}`}
                >
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${dot}`}></span>
                      <div>
                        <div className="font-semibold">
                          {c.curso_nome}
                          {periodoTipo === "ano" && c.mes_ano && (
                            <span className="text-[10px] font-normal text-slate-400 ml-1.5">· {c.mes_ano}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{c.nucleo_nome}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-right tabular-nums">{c.meta_inscritos}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{formatBRL(c.meta_receita)}</td>
                  <td className="py-3 px-3 text-right tabular-nums font-bold">{formatBRL(c.realizado)}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{c.inscritos}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{formatBRL(c.ticket_medio)}</td>
                  <td className={`py-3 px-3 text-right font-bold tabular-nums ${pctc >= 100 ? "text-emerald-600" : pctc >= 50 ? "text-amber-600" : "text-red-600"}`}>
                    {pctc.toFixed(0)}%
                  </td>
                  <td className="py-3 px-3 text-right tabular-nums text-slate-500">{formatBRL(c.exp_min)}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-slate-500">{formatBRL(c.exp_max)}</td>
                  <td className="py-3 px-3 text-right">{c.interessados}</td>
                  <td className="py-3 px-3 text-right">{c.incertos}</td>
                  <td className="py-3 px-3 text-right">{c.prospectados}</td>
                </tr>
              );
            })}
            {/* TOTAL — cores idem cabeçalho */}
            <tr className="font-bold text-white">
              <td className="py-3 px-3 uppercase tracking-widest text-xs bg-slate-700">
                Total {periodoTipo === "ano" ? "do ano" : "do mês"}
              </td>
              <td className="py-3 px-3 text-right tabular-nums bg-blue-900">{t.meta_inscritos}</td>
              <td className="py-3 px-3 text-right tabular-nums bg-blue-900">{formatBRL(t.meta_receita)}</td>
              <td className="py-3 px-3 text-right tabular-nums bg-emerald-600">{formatBRL(t.realizado)}</td>
              <td className="py-3 px-3 text-right tabular-nums bg-emerald-600">{t.inscritos}</td>
              <td className="py-3 px-3 text-right tabular-nums bg-emerald-600">{formatBRL(ticket_medio_f)}</td>
              <td className="py-3 px-3 text-right tabular-nums bg-emerald-600">{pct_f.toFixed(0)}%</td>
              <td className="py-3 px-3 text-right tabular-nums bg-amber-500">{formatBRL(t.exp_min)}</td>
              <td className="py-3 px-3 text-right tabular-nums bg-amber-500">{formatBRL(t.exp_max)}</td>
              <td className="py-3 px-3 text-right bg-sky-400">{t.interessados}</td>
              <td className="py-3 px-3 text-right bg-slate-900">{t.incertos}</td>
              <td className="py-3 px-3 text-right bg-slate-400">{t.prospectados}</td>
            </tr>
          </tbody>
        </table>
      </Card>

      {/* Ranking de Produtividade e Resultado — mesclando o antigo Dash de Guerra */}
      <ProdutividadeResultadoTable
        periodoTipo={periodoTipo}
        mesAno={mesAno}
        ano={anoSel}
        nucleoId={filtroNucleo}
        vendedoraId={filtroVend}
        currentUser={user}
      />

      {/* Rankings — DEPOIS dos cursos */}
      <div>
        <Card className="p-6 rounded-2xl" data-testid="ranking-vendas">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h2 className="font-display font-bold text-xl text-slate-900">Ranking de Vendas</h2>
          </div>
          {rankingsFiltered.vendas.length > 0 ? <Podium items={rankingsFiltered.vendas} /> : <div className="text-slate-400 text-sm py-4">Sem vendas ainda no período.</div>}
          <div className="mt-2 space-y-1">
            {rankingsFiltered.vendas.slice(3, 10).map((r, i) => (
              <div key={r.vendedora_id} className="flex items-center justify-between text-sm py-2 px-2 rounded hover:bg-slate-50">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 font-bold w-5">{i + 4}</span>
                  <Avatar className="w-6 h-6"><AvatarFallback className="text-[10px] bg-slate-200">{initials(r.nome)}</AvatarFallback></Avatar>
                  <span>{r.nome}</span>
                  <NucleoTag item={r} small />
                </div>
                <span className="font-bold tabular-nums">{formatBRL(r.valor)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6 rounded-2xl" data-testid="chart-evolucao">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl text-slate-900">
              {isYoY
                ? (periodoTipo === "ano"
                    ? `Evolução mensal · ${anoSel} vs ${anoSel - 1}`
                    : (() => {
                        const [ys, ms] = mesAno.split("-");
                        return `Evolução diária · ${mesAno} vs ${Number(ys) - 1}-${ms}`;
                      })())
                : "Evolução diária da receita"}
            </h2>
            {isYoY && (
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-1 rounded bg-blue-600"></span>{periodoTipo === "ano" ? anoSel : mesAno}</span>
                <span className="flex items-center gap-1"><span className="w-3 h-1 rounded bg-slate-400"></span>{periodoTipo === "ano" ? anoSel - 1 : `${Number(mesAno.split("-")[0]) - 1}-${mesAno.split("-")[1]}`}</span>
              </div>
            )}
          </div>
          <div className="h-64" style={{ minHeight: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              {isYoY ? (
                <LineChart data={evolucaoYoY}>
                  <XAxis dataKey="mes" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatBRL(v)} />
                  <Line type="monotone" dataKey="anterior" name="Ano anterior" stroke="#94A3B8" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="atual" name="Atual" stroke="#2563EB" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              ) : (
                <LineChart data={data.evolucao_diaria}>
                  <XAxis dataKey="dia" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatBRL(v)} />
                  <Line type="monotone" dataKey="valor" stroke="#2563EB" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl" data-testid="chart-motivos">
          <h2 className="font-display font-bold text-xl text-slate-900 mb-4">Motivos de perda</h2>
          {data.motivos_perda.length > 0 ? (
            <div className="h-64" style={{ minHeight: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.motivos_perda} dataKey="count" nameKey="categoria" cx="50%" cy="50%" outerRadius={80}>
                    {data.motivos_perda.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
              Nenhuma perda registrada ainda 💪
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/**
 * Ranking de Produtividade e Resultado — antigo Dash de Guerra embutido no Dashboard.
 * Fetch dedicado a /api/dashboards/guerra respeitando os mesmos filtros aplicados na página.
 */
function ProdutividadeResultadoTable({ periodoTipo, mesAno, ano, nucleoId, vendedoraId, currentUser }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (periodoTipo === "ano") p.set("ano", ano);
    else p.set("mes_ano", mesAno);
    if (nucleoId && nucleoId !== "todos") p.set("nucleo_id", nucleoId);
    api.get(`/dashboards/guerra?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [periodoTipo, mesAno, ano, nucleoId]);

  if (!data) {
    return <div className="text-slate-400 text-sm py-4">Carregando ranking...</div>;
  }

  let linhas = data.linhas;
  if (vendedoraId && vendedoraId !== "todos") {
    linhas = linhas.filter((l) => l.vendedora_id === vendedoraId);
  }
  // Vendedora vê o próprio destaque no topo (todas as vendas dela), mas mantém a lista do núcleo abaixo
  const minhaLinha = currentUser?.role === "vendedora"
    ? (data.minha_linha || data.linhas.find((l) => l.vendedora_id === currentUser.id))
    : null;
  const ou = (v, fmt = (x) => x) => (v === null || v === undefined ? "—" : fmt(v));

  return (
    <Card className="p-6 rounded-2xl overflow-x-auto" data-testid="prod-resultado">
      <div className="flex items-center gap-2 mb-4">
        <Swords className="w-5 h-5 text-red-500" />
        <h2 className="font-display font-bold text-xl text-slate-900">Ranking de Produtividade e Resultado</h2>
        <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
          {linhas.length}
        </span>
      </div>

      {minhaLinha && (
        <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200" data-testid="minha-linha">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-indigo-600">
                Você · Meta {periodoTipo === "ano" ? "anual" : "do mês"}
              </div>
              <div
                className="font-display font-bold tabular-nums text-slate-900 mt-1 whitespace-nowrap overflow-hidden text-ellipsis"
                style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)", lineHeight: 1.15 }}
                title={formatBRL(minhaLinha.valor_vendido)}
              >
                {formatBRL(minhaLinha.valor_vendido)}
              </div>
              <div className="text-sm text-slate-600 mt-0.5">
                de {formatBRL(minhaLinha.meta)} · <b className={minhaLinha.percentual_meta >= 100 ? "text-emerald-600" : "text-slate-700"}>{minhaLinha.percentual_meta.toFixed(0)}%</b>
                {minhaLinha.percentual_meta >= 100 && <span className="ml-2 text-emerald-600 font-bold">🏆 Meta batida!</span>}
              </div>
            </div>
            <div className="min-w-[240px] w-full md:w-1/3">
              <ProgressBar value={minhaLinha.valor_vendido} total={minhaLinha.meta} big />
            </div>
          </div>
        </div>
      )}

      <table className="w-full text-sm min-w-[900px]" data-testid="tabela-prod-resultado">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
            <th className="pb-3 pr-3">Vendedora</th>
            <th className="pb-3 pr-3 text-right">Prospect.</th>
            <th className="pb-3 pr-3 text-right"><Phone className="w-3 h-3 inline mr-1" />Lig.</th>
            <th className="pb-3 pr-3 text-right"><MessageCircle className="w-3 h-3 inline mr-1" />WA</th>
            <th className="pb-3 pr-3 text-right">Inscritos</th>
            <th className="pb-3 pr-3 text-right">Valor</th>
            <th className="pb-3 pr-3 text-right">Desc.</th>
            <th className="pb-3 pr-3 text-right">Ticket</th>
            <th className="pb-3 pr-3 text-right">Conv.</th>
            <th className="pb-3 pr-3 text-right">Meta</th>
            <th className="pb-3 text-right">% Meta</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr
              key={l.vendedora_id}
              className={`border-b border-slate-100 ${(l.percentual_meta || 0) >= 100 ? "bg-emerald-50/50" : ""} ${currentUser?.id === l.vendedora_id ? "bg-indigo-50/50 font-semibold" : ""} ${l.do_nucleo ? "border-l-4 border-l-blue-600" : ""}`}
              data-testid={`prod-row-${l.vendedora_id}`}
            >
              <td className="py-3 pr-3">
                <div className="flex items-center gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={resolveAvatar(l.avatar_url)} alt={l.nome} />
                    <AvatarFallback className="text-xs bg-blue-100 text-blue-700 font-bold">{initials(l.nome)}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start gap-0.5">
                    <span>{l.nome}</span>
                    <NucleoTag item={l} />
                  </div>
                </div>
              </td>
              <td className="py-3 pr-3 text-right tabular-nums">{ou(l.prospectados)}</td>
              <td className="py-3 pr-3 text-right tabular-nums">{ou(l.ligacoes)}</td>
              <td className="py-3 pr-3 text-right tabular-nums">{ou(l.whatsapps)}</td>
              <td className="py-3 pr-3 text-right tabular-nums font-semibold">{l.inscritos}</td>
              <td className="py-3 pr-3 text-right tabular-nums font-semibold">{formatBRL(l.valor_vendido)}</td>
              <td className="py-3 pr-3 text-right tabular-nums text-slate-500">{formatBRL(l.desconto)}</td>
              <td className="py-3 pr-3 text-right tabular-nums">{formatBRL(l.ticket_medio)}</td>
              <td className="py-3 pr-3 text-right tabular-nums">{ou(l.taxa_conversao, (v) => `${v.toFixed(0)}%`)}</td>
              <td className="py-3 pr-3 text-right tabular-nums">{ou(l.meta, formatBRL)}</td>
              <td className="py-3 text-right">
                {l.percentual_meta === null || l.percentual_meta === undefined ? (
                  <span className="text-slate-400">—</span>
                ) : (
                  <div className="flex items-center justify-end gap-2">
                    <span className={`font-bold tabular-nums ${l.percentual_meta >= 100 ? "text-emerald-600" : "text-slate-700"}`}>
                      {l.percentual_meta.toFixed(0)}%
                    </span>
                    <div className="w-16"><ProgressBar value={l.valor_vendido} total={l.meta} /></div>
                    {l.percentual_meta >= 100 && <Trophy className="w-4 h-4 text-amber-500" />}
                  </div>
                )}
              </td>
            </tr>
          ))}
          {linhas.length === 0 && (
            <tr><td colSpan={11} className="text-center text-slate-400 py-6">Sem dados no período.</td></tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

